import $ from "zeptojs";
import extractVideoRoot from "utils/extractVideoRoot";
import generateImageLink from "utils/generateImageLink";
import generatePosterUrlFromVideoRoot from "utils/generatePosterUrlFromVideoRoot";
import getNeedsDashJs from "selectors/getNeedsDashJs";
import getRequireVerseThree from "selectors/loading/getRequireVerseThree";
import ImageAspectRatioCacheContextProvider from "context/ImageAspectRatioCacheContextProvider";
import MediaContextProvider from "context/MediaContextProvider";
import normalizeStory from "utils/normalizeStory";
import parsePlayerOptions from "utils/parsePlayerOptions";
import postprocessNormalizedStory from "utils/postprocessNormalizedStory";
import React from "react";
import ReactDOM from "react-dom";
import store from "utils/storeFactory";
import { storyPublishSettingsLoaded } from "actions/StoryPublishSettingsActions";
import Tracker from "utils/Analytics/Tracker";
import VideoPlayer from "components/VideoPlayer";
import { AnalyticsActions } from "utils/Analytics";
import {
  dashJsLoadComplete,
  verseThreeLoadComplete,
} from "actions/LoadingActions";
import { Provider } from "react-redux";
import { setForceChapterCover } from "actions/ForceChapterCoverActions";
import { setPlayerOptions } from "actions/PlayerOptionsActions";
import { setUserAgent } from "actions/DeviceActions";
import { setViewportDimensions } from "actions/ViewportActions";
import { storyLoaded } from "actions/StoryActions";
import { changePlaybackState } from "actions/PlaybackStateActions";
import * as UserInteractionActions from "actions/UserInteractionActions";
import { setVolumeInitiallyMuted, setVolumeMuted } from "actions/VolumeActions";
import getCanInitiallyAutoplay from "selectors/device/getCanInitiallyAutoplay";
import getIsMobile from "selectors/device/getIsMobile";

const chapterId = -1;
const stateKey = `chapter-${chapterId}`;

export default class SingleVideoPlayer {
  constructor(options = {}) {
    if (!options.target) {
      throw Error(
        "Video player requires 'target' property assigned with DOM element",
      );
    }

    if (!options.video || !options.duration) {
      throw Error(
        "Video player requires 'video' object property and 'duration' number property",
      );
    }

    const playerOptions = parsePlayerOptions(
      {
        ...options,
        singleVideoPlayer: true,
      },
      {
        windowLocationSearch: window.location.search,
      },
    );

    Tracker.setPersistentData({
      title: options.title,
      userAgent: window.navigator.userAgent,
      uniqueId: options.uniqueId,
    });
    Tracker.setIsSingleVideoPlayer(true);
    Tracker.setViewer(options.viewer);

    // initial player setup
    store.dispatch(setUserAgent(window.navigator.userAgent));
    this.handleResize();

    const shouldForceCover = playerOptions.embedded && !playerOptions.autoplay;

    if (!getCanInitiallyAutoplay(store.getState()) && !shouldForceCover) {
      store.dispatch(setVolumeMuted(true));
      store.dispatch(setVolumeInitiallyMuted(true));
    }

    if (shouldForceCover) {
      store.dispatch(setForceChapterCover(true));
    }

    if (getNeedsDashJs(store.getState())) {
      import(
        /* webpackChunkName: "videojs-contrib-dash" */ "videojs-contrib-dash"
      ).then(() => {
        store.dispatch(dashJsLoadComplete());
      });
    }

    let image = playerOptions.image;
    if (!image) {
      const videoRoot = extractVideoRoot(playerOptions.video.src[0]);
      let imageUrl = generatePosterUrlFromVideoRoot(videoRoot);
      imageUrl = generateImageLink(imageUrl, {
        isMobile: getIsMobile(store.getState()),
      });
      image = {
        ...playerOptions.video,
        src: imageUrl,
        mediaType: "image",
      };
    }

    const video = {
      ...playerOptions.video,
    };

    let customizationExtensions = {
      customizations: {
        playerNavigation: {
          allowSharing: false,
          showHamburger: false,
        },
      },
    };

    if (playerOptions.customizations) {
      customizationExtensions.customizations = {
        ...customizationExtensions.customizationExtensions,
        ...options.customizations,
      };

      customizationExtensions.customColors = {
        ...options.customizations.customColors,
      };

      customizationExtensions = {
        ...customizationExtensions,
        ...options.customizations.logo,
      };
    }

    const dataStub = {
      video: [
        {
          ...video,
        },
      ],
      chapterStore: [
        {
          id: chapterId,
          type: "video",
          video: [
            {
              ...video,
            },
          ],
          thumbnail: [image],
          duration: playerOptions.duration,
          title: playerOptions.title || "",
        },
      ],
      ...customizationExtensions,
      topLevelChapterIds: [chapterId],
    };

    postprocessNormalizedStory(normalizeStory(dataStub)).then(
      (normalizedStub) => {
        this.handleResize();
        window.addEventListener("resize", this.handleResize);
        window.addEventListener("orientationchange", this.handleResize);
        window.addEventListener("scroll", this.handleScroll);

        store.dispatch(storyLoaded(normalizedStub));
        // Set redux player options
        store.dispatch(setPlayerOptions(playerOptions));

        if (options.publishSettings) {
          store.dispatch(storyPublishSettingsLoaded(options.publishSettings));
        }

        AnalyticsActions.singleVideoLoaded();

        if (getRequireVerseThree(store.getState())) {
          import(/* webpackChunkName: "verse-three" */ "verse-three").then(
            () => {
              store.dispatch(verseThreeLoadComplete());
            },
          );
        }

        const player = (
          <Provider store={store}>
            <MediaContextProvider>
              <ImageAspectRatioCacheContextProvider>
                <VideoPlayer chapterId={chapterId} />
              </ImageAspectRatioCacheContextProvider>
            </MediaContextProvider>
          </Provider>
        );

        ReactDOM.render(player, playerOptions.target);
      },
    );
  }

  handleResize() {
    store.dispatch(
      setViewportDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      }),
    );
  }

  handleScroll() {
    const $body = $("body");
    if ($body.scrollTop() !== 0) {
      $body.scrollTop(0);
    }
  }

  static play() {
    store.dispatch(UserInteractionActions.receivedUserInteraction());
    store.dispatch(setForceChapterCover(false));
    store.dispatch(UserInteractionActions.userInteractionInitiatedPlayback());
    store.dispatch(changePlaybackState({ stateKey }, { playing: true }));
  }

  static pause() {
    store.dispatch(changePlaybackState({ stateKey }, { playing: false }));
  }

  static seek(time) {
    store.dispatch(changePlaybackState({ stateKey }, { time }));
  }
}
